/**
 * Dar por HECHOS pasos de la configuración de cuenta paga, en local y sin tocar
 * la base — supersede el `devSkipBanner` del mismo día (ADR-2026-08-27-tarjeta-configuracion-cuenta-paga).
 *
 * ⚠️ SÓLO DEV, mismo patrón que `devSkipOnboarding`: todo está guardado por
 * `import.meta.env.DEV`, así que en el build de producción `pasoForzadoHecho()`
 * es constante `false`, los `if` que la consultan se podan y la tarjeta se
 * comporta exactamente como antes.
 *
 * Para qué: los dos pasos (marca, Tienda Nube) se cierran con datos de la DB
 * —`config.media_agent_analysis`, la integración de Tiendanube— y la cuenta con
 * la que se verifica es la real, de un plan pago. Para mirar "1 de 2", "2 de 2"
 * o la felicitación había que completarlos de verdad o editar producción.
 *
 * Cómo se prende (el que te quede más a mano):
 *   - URL:     http://localhost:5173/inicio?setup-hecho=marca
 *              varios: `?setup-hecho=marca,tiendanube` — con los dos sale la
 *              felicitación, que es el estado más difícil de alcanzar a mano.
 *              todos: `?setup-hecho=1` · apagar: `?setup-hecho=0`
 *   - consola: atlasSetupHecho('marca') / atlasSetupHecho(false)
 *
 * Y al revés, para ver la tarjeta como la ve alguien que todavía no hizo nada —el
 * caso más difícil de mirar desde una cuenta que ya está configurada—:
 *   - URL:     http://localhost:5173/inicio?setup-pendiente=marca
 *              varios: `?setup-pendiente=marca,tiendanube` (o `=1` para los dos)
 *              apagar: `?setup-pendiente=0`
 *   - consola: atlasSetupPendiente('marca') / atlasSetupPendiente(false)
 *
 * "Pendiente" le gana a "hecho" si un paso está en las dos listas: es el que se
 * pidió más explícitamente.
 *
 * Los dos flags quedan en localStorage (por navegador) hasta que los apagues.
 *
 * Con algún paso forzado, `useHomeBanners` NO escribe los flags de
 * `onboarding_seen` ("en curso" / "ya te felicitamos"): mirar la felicitación en
 * local no puede gastarle el festejo real a la cuenta.
 */

import type { SetupPasoId } from '../hooks/useHomeBanners';

const KEY = 'atlas-dev-setup-hecho';
const PARAM = 'setup-hecho';
const KEY_PENDIENTE = 'atlas-dev-setup-pendiente';
const PARAM_PENDIENTE = 'setup-pendiente';
const VALIDOS: SetupPasoId[] = ['marca', 'tiendanube'];

/** `true` sólo en `npm run dev`. En el build es `false` y todo esto se poda. */
export const DEV_HOME_SETUP_ENABLED = import.meta.env.DEV;

function leerFlags(clave = KEY): SetupPasoId[] {
  try {
    const raw = localStorage.getItem(clave);
    if (!raw) return [];
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is SetupPasoId => (VALIDOS as string[]).includes(s));
  } catch {
    return [];
  }
}

function escribirFlags(pasos: SetupPasoId[], clave = KEY): void {
  try {
    if (pasos.length) localStorage.setItem(clave, pasos.join(','));
    else localStorage.removeItem(clave);
  } catch {
    /* noop */
  }
}

/** Normaliza lo que venga de la URL o de la consola a una lista de pasos válidos. */
function parsear(valor: string): SetupPasoId[] {
  if (valor === '0' || valor === 'false' || valor === '') return [];
  // `1`/`true`/`todo` = los dos pasos hechos → sale la felicitación.
  if (valor === '1' || valor === 'true' || valor === 'todo') return [...VALIDOS];
  return valor
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is SetupPasoId => (VALIDOS as string[]).includes(s));
}

/**
 * ¿Hay que dar este paso por hecho aunque la DB diga que no?
 *
 * Se lee de forma síncrona dentro del efecto de `useHomeBanners` (no es un hook):
 * los pasos se resuelven en un async y no pueden depender del orden de render.
 */
export function pasoForzadoHecho(paso: SetupPasoId): boolean {
  if (!DEV_HOME_SETUP_ENABLED) return false;
  if (pasoForzadoPendiente(paso)) return false; // "pendiente" manda
  return leerFlags().includes(paso);
}

/**
 * ¿Hay que mostrar este paso como PENDIENTE aunque la DB diga que está hecho?
 *
 * Es el inverso de `pasoForzadoHecho` y existe porque desde una cuenta que ya está
 * configurada no hay forma de ver el arranque del recorrido —el "0 de 2" que ve
 * alguien recién suscripto— sin desarmarle los datos.
 */
export function pasoForzadoPendiente(paso: SetupPasoId): boolean {
  if (!DEV_HOME_SETUP_ENABLED) return false;
  return leerFlags(KEY_PENDIENTE).includes(paso);
}

/**
 * Aplica `?setup-hecho=...` si viene en la URL y limpia el parámetro para que no
 * se arrastre en los links. Se llama una vez al bootear la app, junto con el
 * bypass de onboarding.
 */
export function aplicarSetupHechoDeLaURL(): void {
  if (!DEV_HOME_SETUP_ENABLED) return;
  try {
    const url = new URL(window.location.href);
    let tocado = false;
    for (const [param, clave] of [
      [PARAM, KEY],
      [PARAM_PENDIENTE, KEY_PENDIENTE],
    ] as const) {
      const valor = url.searchParams.get(param);
      if (valor === null) continue;
      escribirFlags(parsear(valor), clave);
      url.searchParams.delete(param);
      tocado = true;
    }
    if (tocado) window.history.replaceState({}, '', url.toString());
  } catch {
    /* noop */
  }
}

/**
 * Helpers de consola: `atlasSetupHecho('marca')` y `atlasSetupPendiente('marca')`
 * (con `false` cada uno apaga el suyo).
 */
export function registrarHelperDeSetupEnConsola(): void {
  if (!DEV_HOME_SETUP_ENABLED) return;
  const helper =
    (clave: string, etiqueta: string) =>
    (valor: string | boolean = true) => {
      const pasos =
        typeof valor === 'boolean' ? (valor ? [...VALIDOS] : []) : parsear(valor);
      escribirFlags(pasos, clave);
      console.log(
        pasos.length
          ? `[dev] Pasos forzados a ${etiqueta}: ${pasos.join(', ')}. Recargá la página.`
          : `[dev] Ningún paso forzado a ${etiqueta}. Recargá la página.`,
      );
      return pasos;
    };
  (window as any).atlasSetupHecho = helper(KEY, 'hechos');
  (window as any).atlasSetupPendiente = helper(KEY_PENDIENTE, 'pendientes');
}
